/** Site chrome: the paper-toned header with the wordmark, navigation and
 *  language menu, and the dark three-column footer. */

import { useEffect, useRef, useState, type ReactElement } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import type { SiteConfig } from '../lib/api'
import {
  LANGUAGE_NAMES,
  LOCALES,
  detectLocale,
  isLocalizablePath,
  localeFromPath,
  pathFor,
  rememberLocale,
  storedLocale,
  switchLocalePath,
  t,
} from '../lib/i18n'
import { useLocale, useSiteConfig } from '../lib/hooks'
import { usePageView } from '../lib/track'
import { consentRelevant, useConsent, useGoogleAnalytics } from '../lib/gtag'
import ConsentBanner from './ConsentBanner'

/** The owner's maker's mark: his Z inside two rings, as branded on his
 *  instruments. */
const LOGO = '/media/Z-mark.png'

const NAV_ITEMS = ['uebermich', 'klang', 'galerie', 'shop', 'service', 'kontakt'] as const

function Header() {
  const locale = useLocale()
  const location = useLocation()
  const strings = t(locale)
  const [menuOpen, setMenuOpen] = useState(false)

  // Close the mobile menu whenever the route changes.
  useEffect(() => setMenuOpen(false), [location.pathname])

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex h-[72px] items-center border-b-2 pt-[2px] font-sans text-[15px] font-semibold transition-colors duration-200 ${
      isActive ? 'border-amber text-ink' : 'border-transparent text-slate hover:text-ink'
    }`

  return (
    <header className="sticky top-0 z-40 h-[72px] border-b border-line bg-paper">
      <div className="mx-auto flex h-full max-w-[1200px] items-center justify-between gap-6 px-6 md:px-10">
        <Link
          to={pathFor(locale, 'home')}
          className="flex shrink-0 items-center gap-3"
          aria-label="Zvi Dori Geigenbau"
        >
          <img src={LOGO} alt="" className="h-[50px] w-[50px]" />
          <span className="flex flex-col leading-none">
            <span className="font-serif text-[23px] font-medium tracking-[0.01em] text-ink">Zvi Dori</span>
            <span className="mt-[3px] font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-amber">
              Geigenbau
            </span>
          </span>
        </Link>

        <nav className="hidden h-full items-center gap-[26px] lg:flex" aria-label={strings.menu}>
          {NAV_ITEMS.map((key) => (
            <NavLink key={key} to={pathFor(locale, key)} className={linkClass} end>
              {strings.nav[key]}
            </NavLink>
          ))}
          <LanguageSwitch />
        </nav>

        <button
          type="button"
          className="-mr-2 p-2 text-ink lg:hidden"
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          aria-label={menuOpen ? strings.closeMenu : strings.menu}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {menuOpen ? <path d="M18 6 6 18M6 6l12 12" /> : <path d="M3 6h18M3 12h18M3 18h18" />}
          </svg>
        </button>
      </div>

      {menuOpen ? (
        <div id="mobile-menu" className="border-t border-line bg-paper-deep lg:hidden">
          <nav className="flex flex-col px-6 py-3" aria-label={strings.menu}>
            {NAV_ITEMS.map((key) => (
              <NavLink
                key={key}
                to={pathFor(locale, key)}
                className={({ isActive }) =>
                  `border-b border-line py-3.5 font-sans text-[17px] font-semibold last:border-0 ${
                    isActive ? 'text-ink' : 'text-slate'
                  }`
                }
                end
              >
                {strings.nav[key]}
              </NavLink>
            ))}
            <div className="pt-4">
              <LanguageSwitch inline />
            </div>
          </nav>
        </div>
      ) : null}
    </header>
  )
}

/** Tiny inline flags for the language menu - SVG, not emoji, so they render
 *  on every platform (Windows shows no flag emoji). Simplified at 18×12. */
const FLAGS: Record<string, ReactElement> = {
  de: (
    <svg viewBox="0 0 3 2" className="h-[12px] w-[18px]" aria-hidden="true">
      <rect width="3" height="2" fill="#000" />
      <rect y="0.667" width="3" height="1.333" fill="#D00" />
      <rect y="1.333" width="3" height="0.667" fill="#FFCE00" />
    </svg>
  ),
  en: (
    <svg viewBox="0 0 60 40" className="h-[12px] w-[18px]" aria-hidden="true">
      <rect width="60" height="40" fill="#012169" />
      <path d="M0,0 60,40M60,0 0,40" stroke="#fff" strokeWidth="8" />
      <path d="M0,0 60,40M60,0 0,40" stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 V40M0,20 H60" stroke="#fff" strokeWidth="13" />
      <path d="M30,0 V40M0,20 H60" stroke="#C8102E" strokeWidth="8" />
    </svg>
  ),
  ja: (
    <svg viewBox="0 0 3 2" className="h-[12px] w-[18px]" aria-hidden="true">
      <rect width="3" height="2" fill="#fff" />
      <circle cx="1.5" cy="1" r="0.6" fill="#BC002D" />
    </svg>
  ),
  ko: (
    <svg viewBox="0 0 3 2" className="h-[12px] w-[18px]" aria-hidden="true">
      <rect width="3" height="2" fill="#fff" />
      {/* Simplified taegeuk - the trigrams are invisible at this size. */}
      <path d="M1 1 A0.5 0.5 0 0 1 2 1 A0.25 0.25 0 0 0 1.5 1 A0.25 0.25 0 0 1 1 1" fill="#CD2E3A" />
      <path d="M2 1 A0.5 0.5 0 0 1 1 1 A0.25 0.25 0 0 1 1.5 1 A0.25 0.25 0 0 0 2 1" fill="#0047A0" />
    </svg>
  ),
  zh: (
    <svg viewBox="0 0 30 20" className="h-[12px] w-[18px]" aria-hidden="true">
      <rect width="30" height="20" fill="#DE2910" />
      <path d="M5 2 l1.18 3.63 -3.09 -2.24 h3.82 l-3.09 2.24 Z" fill="#FFDE00" />
      <circle cx="10" cy="2.5" r="0.7" fill="#FFDE00" />
      <circle cx="12" cy="4.5" r="0.7" fill="#FFDE00" />
      <circle cx="12" cy="7" r="0.7" fill="#FFDE00" />
      <circle cx="10" cy="9" r="0.7" fill="#FFDE00" />
    </svg>
  ),
}

/** Five languages don't fit as flags in the header: the desktop nav gets a
 *  small dropdown, the mobile menu renders the same links flat. */
function LanguageSwitch({ inline = false }: { inline?: boolean }) {
  const locale = useLocale()
  const { pathname } = useLocation()
  const strings = t(locale)
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => setOpen(false), [pathname])

  // Close when clicking anywhere else on the page.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const links = LOCALES.map((target) => (
    <Link
      key={target}
      to={switchLocalePath(pathname, target)}
      hrefLang={target}
      lang={target}
      data-testid={`lang-${target}`}
      // An explicit choice beats browser detection on future visits.
      onClick={() => rememberLocale(target)}
      aria-current={locale === target ? 'true' : undefined}
      className={`flex items-center gap-2.5 whitespace-nowrap rounded-[2px] px-4 py-2 font-sans text-[15px] font-semibold leading-normal transition-colors ${
        locale === target ? 'text-ink' : 'text-slate hover:bg-paper-deep hover:text-ink'
      }`}
    >
      <span className="inline-block overflow-hidden rounded-[1px] shadow-[0_0_0_1px_rgba(0,0,0,0.12)]">
        {FLAGS[target]}
      </span>
      {LANGUAGE_NAMES[target]}
    </Link>
  ))

  if (inline) {
    return (
      <nav className="flex flex-wrap gap-x-2 gap-y-1" aria-label={strings.language}>
        {links}
      </nav>
    )
  }

  return (
    <div ref={rootRef} className="relative pl-[6px]">
      <button
        type="button"
        data-testid="lang-switcher"
        aria-expanded={open}
        aria-label={strings.language}
        onClick={() => setOpen((value) => !value)}
        className="flex h-[72px] items-center gap-1 font-sans text-[13px] font-semibold uppercase tracking-[0.1em] text-slate transition-colors hover:text-ink"
      >
        {locale}
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          aria-hidden="true"
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open ? (
        <div
          data-testid="lang-menu"
          className="absolute right-0 top-[60px] z-50 min-w-[150px] rounded-[2px] border border-line bg-paper py-1 shadow-[0_10px_30px_rgba(31,27,24,0.14)]"
        >
          {links}
        </div>
      ) : null}
    </div>
  )
}

const SOCIAL: { key: 'instagram' | 'facebook' | 'youtube'; label: string; icon: ReactElement }[] = [
  {
    key: 'instagram',
    label: 'Instagram',
    // Stroke-built camera mark - a filled outline path renders as a blob.
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4.2" />
        <circle cx="17.2" cy="6.8" r="1.15" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    key: 'facebook',
    label: 'Facebook',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M13.5 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.25-1.5 1.55-1.5h1.65V3.6c-.3 0-1.3-.13-2.45-.13-2.4 0-4.05 1.47-4.05 4.18v2.33H7.5V13h2.7v8h3.3Z" />
      </svg>
    ),
  },
  {
    key: 'youtube',
    label: 'YouTube',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M21.6 7.2a2.5 2.5 0 0 0-1.75-1.75C18.25 5 12 5 12 5s-6.25 0-7.85.45A2.5 2.5 0 0 0 2.4 7.2C2 8.8 2 12 2 12s0 3.2.4 4.8a2.5 2.5 0 0 0 1.75 1.75C5.75 19 12 19 12 19s6.25 0 7.85-.45a2.5 2.5 0 0 0 1.75-1.75C22 15.2 22 12 22 12s0-3.2-.4-4.8ZM10 15V9l5.2 3L10 15Z" />
      </svg>
    ),
  },
]

function Footer({ config }: { config: SiteConfig | null }) {
  const locale = useLocale()
  const strings = t(locale)
  const contact = config?.contact ?? {}
  // A granted consent has to be as easy to take back as it was to give, so
  // the bar is one click away from every page.
  const { reopen } = useConsent()

  const phones = [contact.phone1, contact.phone2].filter(Boolean)

  return (
    <footer className="bg-night px-6 py-16 text-paper md:px-10 md:py-20" data-testid="site-footer">
      <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-12 md:grid-cols-3">
        <address className="not-italic font-sans text-[16px] leading-[1.6] text-paper/80">
          <span className="block font-serif text-[26px] font-medium leading-[1.2] text-paper">
            {contact.name || 'Zvi Dori'}
          </span>
          <span className="mb-4 block">{contact.role || 'Geigenbaumeister'}</span>
          <span className="block">{contact.street || 'Schiffgraben 61'}</span>
          <span className="block">{contact.city || '30175 Hannover'}</span>
        </address>

        <div className="font-sans text-[16px] leading-[1.6] text-paper/80">
          {phones.map((phone) => (
            <a
              key={phone}
              href={`tel:${phone.replace(/\s/g, '')}`}
              className="block transition-colors hover:text-paper"
            >
              {phone}
            </a>
          ))}
          {contact.email ? (
            <a href={`mailto:${contact.email}`} className="mt-4 block transition-colors hover:text-paper">
              {contact.email}
            </a>
          ) : null}
        </div>

        <div>
          <div className="mb-6 flex gap-[6px]">
            {SOCIAL.map(({ key, label, icon }) => {
              const href = contact[key]
              if (!href) return null
              return (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={label}
                  title={strings.footer.follow}
                  className="flex h-[44px] w-[44px] items-center justify-center rounded-[2px] text-paper/80 transition-colors hover:bg-paper/10 hover:text-paper"
                >
                  {icon}
                </a>
              )
            })}
          </div>

          <nav
            className="flex flex-col gap-[0.8em] font-sans text-[15px] font-semibold text-paper/70"
            aria-label={strings.footer.imprint}
          >
            <Link to={pathFor(locale, 'datenschutz')} className="transition-colors hover:text-paper">
              {strings.footer.privacy}
            </Link>
            <Link to={pathFor(locale, 'impressum')} className="transition-colors hover:text-paper">
              {strings.footer.imprint}
            </Link>
            {consentRelevant() ? (
              <button
                type="button"
                onClick={reopen}
                data-testid="consent-settings"
                className="cursor-pointer text-left transition-colors hover:text-paper"
              >
                {strings.consent.settings}
              </button>
            ) : null}
          </nav>
        </div>
      </div>

      <div className="mx-auto mt-14 max-w-[1200px] border-t border-paper/15 pt-6 font-sans text-[14px] text-paper/50">
        © {new Date().getFullYear()} {contact.name || 'Zvi Dori'} · Geigenbau Hannover
      </div>
    </footer>
  )
}

/** First-load language negotiation: an unprefixed (German) URL redirects to
 *  the visitor's language when the browser clearly prefers a supported one -
 *  a remembered switcher choice wins over detection, German stays the
 *  default, and explicit /en|/ja|/ko|/zh URLs are never overridden. */
function useBrowserLocaleRedirect() {
  const navigate = useNavigate()
  const { pathname, search, hash } = useLocation()
  const ran = useRef(false)

  useEffect(() => {
    if (ran.current) return
    ran.current = true
    if (localeFromPath(pathname) !== 'de') return
    if (!isLocalizablePath(pathname)) return

    const target = storedLocale() ?? detectLocale(navigator.languages ?? [navigator.language])
    if (target !== 'de') {
      navigate(switchLocalePath(pathname, target) + search + hash, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

export default function Layout() {
  const locale = useLocale()
  const config = useSiteConfig()
  const strings = t(locale)
  const { pathname } = useLocation()

  useBrowserLocaleRedirect()

  // Counts this page view. Sits here rather than in App so it can only ever
  // cover the public site - the admin is outside this Layout.
  usePageView()
  // The same page view for Google, but only once the visitor has opted in.
  useGoogleAnalytics()

  // Keep <html lang> in step with the route for screen readers and SEO.
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  // Reset scroll on navigation - the router keeps the position by default.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:bg-paper focus:px-4 focus:py-2 focus:text-ink"
      >
        {strings.skipToContent}
      </a>
      <Header />
      <main id="main" className="flex-1">
        <Outlet />
      </main>
      <Footer config={config} />
      <ConsentBanner />
    </div>
  )
}
